import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Grid,
  Paper,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const featuresChecklist = [
  'REST APIs',
  'GraphQL',
  'gRPC',
  'WebSocket',
  'Mock Server',
  'Validations & Tests',
  'Scripts',
  'Environments',
  'Variables',
  'Collection Runner',
  'Data Driven Testing (CSV)',
  'API Flows (Drag & Drop)',
  'Workspaces',
  'Git Source Control',
  'HawkClient CLI',
  'CI / CD Ready',
  'OpenAPI Export',
  'Postman Import',
  'Code Snippets',
  'File System Storage',
  'Documentation',
  'Auth',
];

const AboutTabContent = () => {
  return (
    <Box sx={{ px: { xs: 2, sm: 2, md: 18, lg: 24 }, py: 4 }}>
      {/* INTRO */}
      <Typography variant="body1" sx={{ mb: 3, fontSize: 16 }}>
        <strong>HawkClient</strong> is a modern, <strong>offline-first</strong>, and
        <strong> Git-friendly API client</strong> built for developers who want
        full control over their API workflows.
        <br />
        <br />
        It stores requests and collections as <strong>.yaml files</strong> directly
        on your local file system, making collaboration through Git seamless and transparent.
      </Typography>

      {/* FEATURE CHECKLIST */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          ✅ Feature Availability
        </Typography>

        <Grid container spacing={1}>
          {featuresChecklist.map(feature => (
            <Grid item xs={6} sm={4} md={3} key={feature}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked
                  icon={<CheckCircleIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  sx={{
                    color: 'success.main',
                    '&.Mui-checked': { color: 'success.main' },
                    p: 0.5,
                  }}
                />
                <Typography variant="body2">{feature}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* DETAILED FEATURES */}
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          🚀 Key Capabilities
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary="Multi-Protocol Support"
              secondary="Test REST, GraphQL, gRPC, and WebSocket APIs from a single interface."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Powerful Scripting Engine"
              secondary="Write pre-request and post-response scripts at request, folder, or collection level using JavaScript."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="API Flows (Drag & Drop)"
              secondary="Visually design complex API workflows with loops, conditions, and branching logic."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Data-Driven Collection Runs"
              secondary="Run collections using CSV input to perform large-scale data-driven testing."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Mock Server"
              secondary="Run a local mock server to simulate API responses without hitting real services. Defaults to localhost:3010 with automatic port fallback."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="File System Storage"
              secondary="Store collections and requests on the filesystem in .yaml format, making it easy to collaborate through version control systems like GitHub."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Built-in Git Source Control"
              secondary="View file changes, diffs, and conflicts directly inside HawkClient, similar to VS Code."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="HawkClient CLI"
              secondary="Run collections from the command line for automation, CI/CD pipelines, and scheduled runs."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="OpenAPI Support"
              secondary="Generate OpenAPI documentation directly from collections."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Offline & Privacy-First"
              secondary="No login, no cloud sync. Everything stays on your machine."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Code Snippet"
              secondary="Automatically generate code snippets for API requests in various programming languages."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="Custom Node Modules"
              secondary="Install node modules inside collection root, which can be accessed in the scripts of that collection."
            />
          </ListItem>

          <ListItem>
            <ListItemText
              primary="File Explorer"
              secondary="Use the built-in file explorer to create and manage JS script files directly within the tool."
            />
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

const About = () => {
  return (
    <>
      <Helmet>
        <title>About - HawkClient</title>
        <meta
          name="description"
          content="HawkClient is an offline-first, Git-native API client supporting REST, GraphQL, gRPC, WebSocket, scripting, flows, and CI/CD automation."
        />
      </Helmet>

      <Box
        sx={{
          mt: 4,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" component="h2" gutterBottom>
          About HawkClient
        </Typography>

        <AboutTabContent />
      </Box>
    </>
  );
};

export default About;
